import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { DEFAULT_CLIENT_SETTINGS } from "@cmmd-forge/contracts/settings";
import { Input } from "../ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import {
  SettingResetButton,
  SettingsPageContainer,
  SettingsRow,
  SettingsSection,
} from "./settingsLayout";
import type { AgentOrchestrationSettings } from "@cmmd-forge/contracts";
import { searchableSetting } from "./settingsSearch";

const PLAN_APPROVAL_LABELS = {
  require: "Always require approval",
  autoAtWorkspace: "Auto-approve in current project",
  autoEverywhere: "Auto-approve everywhere",
} as const;

const WORKER_SPAWN_LABELS = {
  require: "Always require approval",
  autoAfterPlan: "Auto-spawn after plan approval",
  autoEverywhere: "Auto-spawn always",
} as const;

const MERGE_STRATEGY_LABELS = {
  linear: "Linear (fast-forward only)",
  rebaseOnConflict: "Rebase on conflict",
  ask: "Ask on each merge",
} as const;

const DIRECT_MESSAGING_LABELS = {
  warn: "Show soft banner",
  allow: "Allow silently",
  block: "Block direct messages",
} as const;

type NumericOrchField = "stuckTimeoutMin" | "notificationDedupeWindowSec" | "maxWorkersPerMaster";
const NUMERIC_FIELD_BOUNDS: Record<NumericOrchField, { min: number; max: number; step?: number }> =
  {
    stuckTimeoutMin: { min: 5, max: 240 },
    notificationDedupeWindowSec: { min: 5, max: 300 },
    maxWorkersPerMaster: { min: 1, max: 20 },
  };

function NumericInput({
  field,
  orch,
  onUpdate,
}: {
  field: NumericOrchField;
  orch: AgentOrchestrationSettings;
  onUpdate: (patch: Partial<AgentOrchestrationSettings>) => void;
}) {
  const { min, max, step } = NUMERIC_FIELD_BOUNDS[field];
  return (
    <Input
      type="number"
      className="w-20 text-right"
      min={min}
      max={max}
      step={step ?? 1}
      value={orch[field]}
      onChange={(event) => {
        const parsed = Number.parseInt(event.target.value, 10);
        if (!Number.isNaN(parsed) && parsed >= min && parsed <= max) {
          onUpdate({ [field]: parsed });
        }
      }}
      aria-label={field}
    />
  );
}

export function AgentOrchestrationPanel() {
  return (
    <SettingsPageContainer>
      <AgentOrchestrationSection />
    </SettingsPageContainer>
  );
}

export function AgentOrchestrationSection() {
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();
  const orch = settings.agentOrchestration;
  const defaults = DEFAULT_CLIENT_SETTINGS.agentOrchestration;

  const patch = (update: Partial<AgentOrchestrationSettings>) =>
    updateSettings({ agentOrchestration: { ...orch, ...update } });

  const reset = (field: keyof AgentOrchestrationSettings) =>
    orch[field] !== defaults[field] ? (
      <SettingResetButton
        label={String(field)}
        onClick={() => patch({ [field]: defaults[field] } as Partial<AgentOrchestrationSettings>)}
      />
    ) : null;

  return (
    <>
      <SettingsSection id="orchestration" title="Orchestration">
        <SettingsRow
          {...searchableSetting("workflow-plan-approval")}
          description="Whether the master agent's plan requires manual approval before workers are spawned."
          resetAction={reset("planApproval")}
          control={
            <Tooltip>
              <TooltipTrigger
                render={
                  <Select
                    value={orch.planApproval}
                    onValueChange={(v) => {
                      if (v === "require" || v === "autoAtWorkspace" || v === "autoEverywhere") {
                        patch({ planApproval: v });
                      }
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-64" aria-label="Plan approval mode">
                      <SelectValue>{PLAN_APPROVAL_LABELS[orch.planApproval]}</SelectValue>
                    </SelectTrigger>
                    <SelectPopup align="end" alignItemWithTrigger={false}>
                      {(["require", "autoAtWorkspace", "autoEverywhere"] as const).map((v) => (
                        <SelectItem hideIndicator key={v} value={v}>
                          {PLAN_APPROVAL_LABELS[v]}
                        </SelectItem>
                      ))}
                    </SelectPopup>
                  </Select>
                }
              />
              <TooltipPopup side="top" className="max-w-72">
                Destructive operations always require fresh confirmation regardless of this setting.
              </TooltipPopup>
            </Tooltip>
          }
        />

        <SettingsRow
          {...searchableSetting("workflow-worker-spawn")}
          description="When worker threads are automatically spawned after a plan is approved."
          resetAction={reset("workerSpawn")}
          control={
            <Select
              value={orch.workerSpawn}
              onValueChange={(v) => {
                if (v === "require" || v === "autoAfterPlan" || v === "autoEverywhere") {
                  patch({ workerSpawn: v });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-64" aria-label="Worker spawn mode">
                <SelectValue>{WORKER_SPAWN_LABELS[orch.workerSpawn]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {(["require", "autoAfterPlan", "autoEverywhere"] as const).map((v) => (
                  <SelectItem hideIndicator key={v} value={v}>
                    {WORKER_SPAWN_LABELS[v]}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          {...searchableSetting("workflow-merge-strategy")}
          description="How the master handles merging a worker branch after it reports DONE."
          resetAction={reset("autoMergeWorkers")}
          control={
            <Select
              value={orch.autoMergeWorkers}
              onValueChange={(v) => {
                if (v === "linear" || v === "rebaseOnConflict" || v === "ask") {
                  patch({ autoMergeWorkers: v });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-52" aria-label="Merge strategy">
                <SelectValue>{MERGE_STRATEGY_LABELS[orch.autoMergeWorkers]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {(["linear", "rebaseOnConflict", "ask"] as const).map((v) => (
                  <SelectItem hideIndicator key={v} value={v}>
                    {MERGE_STRATEGY_LABELS[v]}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          {...searchableSetting("workflow-max-workers")}
          description="Maximum number of concurrent worker threads a single master can spawn."
          resetAction={reset("maxWorkersPerMaster")}
          control={<NumericInput field="maxWorkersPerMaster" orch={orch} onUpdate={patch} />}
        />

        <SettingsRow
          {...searchableSetting("workflow-stuck-timeout")}
          description="How long a worker can be silent before the stuck detector marks it as stuck."
          resetAction={reset("stuckTimeoutMin")}
          control={<NumericInput field="stuckTimeoutMin" orch={orch} onUpdate={patch} />}
        />

        <SettingsRow
          {...searchableSetting("workflow-notification-dedupe")}
          description="Worker notifications are coalesced within this window before a single push is sent."
          resetAction={reset("notificationDedupeWindowSec")}
          control={
            <NumericInput field="notificationDedupeWindowSec" orch={orch} onUpdate={patch} />
          }
        />

        <SettingsRow
          {...searchableSetting("workflow-worker-messaging")}
          description="What happens when a user sends a message directly to a worker thread instead of via the master."
          resetAction={reset("workerDirectMessaging")}
          control={
            <Select
              value={orch.workerDirectMessaging}
              onValueChange={(v) => {
                if (v === "warn" || v === "allow" || v === "block") {
                  patch({ workerDirectMessaging: v });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-48" aria-label="Worker direct messaging mode">
                <SelectValue>{DIRECT_MESSAGING_LABELS[orch.workerDirectMessaging]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {(["warn", "allow", "block"] as const).map((v) => (
                  <SelectItem hideIndicator key={v} value={v}>
                    {DIRECT_MESSAGING_LABELS[v]}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          }
        />
      </SettingsSection>
    </>
  );
}
